import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { ArrowUp, Mic, Type, X } from 'lucide-react';
import { getApiBaseUrl } from '../../../utils/apiConfig';
import { useComposerVoiceRecorder } from '../../board-chat/components/ComposerVoiceRecorder';
import { downloadOryaAnswerPdf } from './oryaAnswerPdf';
import './OrderDetailsOryaChat.css';

const YT_ID = /^[A-Za-z0-9_-]{11}$/;
const FIGURA_MARK = /!\[[^\]]*\]\((figura:\d+)\)/g;
const FIGURA_ID = /^figura:(\d+)$/;

function figuraAlt(figura) {
  const page = figura.pagina ? `Figura p.${figura.pagina}` : 'Figura do manual';
  const legenda = String(figura.legenda || '').trim();
  return legenda ? `${page} — ${legenda}` : page;
}

function splitAnswer(content, figuras) {
  const allow = new Map();
  (figuras || []).forEach((figura) => {
    const id = Number(figura?.id);
    if (Number.isInteger(id) && id > 0) allow.set(id, figura);
  });
  const used = new Set();
  const parts = [];
  const text = String(content || '');
  let last = 0;
  for (const match of text.matchAll(FIGURA_MARK)) {
    const id = Number(FIGURA_ID.exec(match[1])?.[1]);
    const figura = allow.get(id);
    if (match.index > last) parts.push(text.slice(last, match.index));
    if (figura && !used.has(id)) {
      used.add(id);
      parts.push(figura);
    }
    last = match.index + match[0].length;
  }
  if (last < text.length) parts.push(text.slice(last));
  const pending = [...allow.values()].filter((figura) => !used.has(Number(figura.id)));
  return { parts, pending };
}

function authHeaders() {
  return { Authorization: `Bearer ${localStorage.getItem('authToken') || ''}` };
}

function api(os, path) {
  return `${getApiBaseUrl()}/api/board/orders/${encodeURIComponent(os)}${path}`;
}

function OryaFigure({ os, figura }) {
  const [src, setSrc] = useState('');
  const [failed, setFailed] = useState(false);
  const [zoom, setZoom] = useState(false);
  const alt = figuraAlt(figura);

  useEffect(() => {
    let dead = false;
    let url = '';
    (async () => {
      try {
        const res = await fetch(api(os, `/orya/figura/${figura.id}`), { headers: authHeaders() });
        if (!res.ok) {
          if (!dead) setFailed(true);
          return;
        }
        url = URL.createObjectURL(await res.blob());
        if (!dead) setSrc(url);
      } catch {
        if (!dead) setFailed(true);
      }
    })();
    return () => {
      dead = true;
      if (url) URL.revokeObjectURL(url);
    };
  }, [os, figura.id]);

  useEffect(() => {
    if (!zoom) return undefined;
    const onKey = (e) => {
      if (e.key !== 'Escape') return;
      e.preventDefault();
      e.stopPropagation();
      if (typeof e.stopImmediatePropagation === 'function') e.stopImmediatePropagation();
      setZoom(false);
    };
    document.addEventListener('keydown', onKey, true);
    return () => document.removeEventListener('keydown', onKey, true);
  }, [zoom]);

  if (failed) return <p className="odm-orya-fig-miss">Figura indisponível</p>;
  if (!src) return null;
  return (
    <figure className="odm-orya-fig">
      <button type="button" className="odm-orya-fig-open" onClick={() => setZoom(true)} aria-label="Ampliar figura">
        <img src={src} alt={alt} />
      </button>
      {figura.legenda ? <figcaption>{figura.legenda}</figcaption> : null}
      {zoom ? createPortal(
        <div className="order-details-mock-anexo-zoom-overlay" onClick={() => setZoom(false)} role="presentation">
          <div className="odm-orya-zoom" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true" aria-label={alt}>
            <button type="button" className="order-details-mock-anexo-zoom-close" onClick={() => setZoom(false)} aria-label="Fechar">
              <X size={16} strokeWidth={2} aria-hidden />
            </button>
            <img src={src} alt={alt} />
          </div>
        </div>,
        document.body,
      ) : null}
    </figure>
  );
}

function AnswerBody({ os, content, figuras }) {
  const { parts, pending } = splitAnswer(content, figuras);
  return (
    <>
      {parts.map((part, index) => (
        typeof part === 'string'
          ? (part.trim() ? <p key={`t-${index}`}>{part.trim()}</p> : null)
          : <OryaFigure key={part.id} os={os} figura={part} />
      ))}
      {pending.map((figura) => <OryaFigure key={figura.id} os={os} figura={figura} />)}
    </>
  );
}

function YoutubeClip({ video }) {
  const id = YT_ID.test(video.id) ? video.id : '';
  const [play, setPlay] = useState(false);
  const [blocked, setBlocked] = useState(false);
  const hostRef = useRef(null);
  const watch = id ? `https://www.youtube.com/watch?v=${id}` : '';

  useEffect(() => {
    if (!play || !id || blocked) return undefined;
    let dead = false;
    let player;
    const mount = () => {
      if (dead || !hostRef.current || !window.YT?.Player) return;
      player = new window.YT.Player(hostRef.current, {
        videoId: id,
        host: 'https://www.youtube-nocookie.com',
        playerVars: { autoplay: 1, playsinline: 1, rel: 0, modestbranding: 1 },
        events: {
          onError: (ev) => {
            if ([101, 150, 153].includes(Number(ev?.data))) setBlocked(true);
          },
        },
      });
    };
    if (window.YT?.Player) mount();
    else {
      const prev = window.onYouTubeIframeAPIReady;
      window.onYouTubeIframeAPIReady = () => {
        if (typeof prev === 'function') prev();
        mount();
      };
      if (!document.querySelector('script[src="https://www.youtube.com/iframe_api"]')) {
        const script = document.createElement('script');
        script.src = 'https://www.youtube.com/iframe_api';
        document.body.appendChild(script);
      }
    }
    return () => {
      dead = true;
      try { player?.destroy(); } catch { /* player já saiu */ }
    };
  }, [play, id, blocked]);

  if (!id) return null;
  return (
    <div className="odm-orya-video">
      {play && !blocked ? <div ref={hostRef} className="odm-orya-video-frame" /> : (
        <button type="button" className="odm-orya-video-open" onClick={() => setPlay(true)}>
          <img src={`https://i.ytimg.com/vi/${id}/hqdefault.jpg`} alt="" />
          <span>{blocked ? 'Este vídeo não pode ser reproduzido aqui' : video.titulo}</span>
        </button>
      )}
      <div className="odm-orya-video-meta">
        <span>{video.canal}</span>
        {blocked ? <a href={watch} target="_blank" rel="noreferrer">Abrir no YouTube</a> : null}
      </div>
    </div>
  );
}

export default function OrderDetailsOryaChat({ os, ready, onClose }) {
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [voiceError, setVoiceError] = useState('');
  const [pdfBusy, setPdfBusy] = useState(-1);
  const listRef = useRef(null);
  const fieldRef = useRef(null);
  const voice = useComposerVoiceRecorder({
    onError: (msg) => setVoiceError(msg || 'Falha no microfone'),
  });

  useEffect(() => {
    const onKey = (e) => {
      if (e.key !== 'Escape') return;
      if (document.querySelector('.order-details-mock-anexo-zoom-overlay')) return;
      e.preventDefault();
      e.stopPropagation();
      if (typeof e.stopImmediatePropagation === 'function') e.stopImmediatePropagation();
      onClose();
    };
    const onDown = (e) => {
      if (!(e.target instanceof Element)) return;
      if (e.target.closest('.odm-orya-anchor')) return;
      if (e.target.closest('.order-details-mock-anexo-zoom-overlay')) return;
      onClose();
    };
    document.addEventListener('keydown', onKey, true);
    document.addEventListener('mousedown', onDown, true);
    return () => {
      document.removeEventListener('keydown', onKey, true);
      document.removeEventListener('mousedown', onDown, true);
    };
  }, [onClose]);

  useEffect(() => {
    const el = listRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [messages, busy]);

  useEffect(() => {
    fieldRef.current?.focus();
  }, []);

  useEffect(() => {
    const el = fieldRef.current;
    if (!el) return;
    el.style.height = 'auto';
    const next = Math.min(el.scrollHeight, 128);
    el.style.height = `${next}px`;
    el.style.overflowY = el.scrollHeight > 128 ? 'auto' : 'hidden';
  }, [text]);

  async function send(event) {
    event.preventDefault();
    const pergunta = text.trim();
    if (!pergunta || busy || !ready) return;
    const historico = messages
      .filter((item) => !item.bloqueado)
      .slice(-6)
      .map((item) => ({ role: item.role, content: item.content }));
    setText('');
    setError('');
    setMessages((prev) => prev.concat({ role: 'user', content: pergunta }));
    setBusy(true);
    try {
      const res = await fetch(api(os, '/orya'), {
        method: 'POST',
        headers: { ...authHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ pergunta, historico }),
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok || !json.success) throw new Error(json.message || 'Não foi possível consultar a Orya.');
      const data = json.data || {};
      setMessages((prev) => prev.concat({
        role: 'assistant',
        content: data.resposta || '',
        fontes: data.fontes || [],
        figuras: data.figuras || [],
        videos: data.videos || [],
        bloqueado: Boolean(data.bloqueado),
      }));
    } catch (err) {
      setError(err.message || 'Não foi possível consultar a Orya.');
    } finally {
      setBusy(false);
    }
  }

  async function onMic() {
    if (voice.busy) return;
    setVoiceError('');
    if (voice.recording) {
      await voice.cancel();
      return;
    }
    await voice.start();
  }

  async function onTranscribe() {
    setVoiceError('');
    try {
      if (!voice.speechSupported) {
        await voice.cancel();
        setVoiceError('Transcrição não suportada neste navegador.');
        return;
      }
      const spoken = String((await voice.finishTranscript()) || '').trim();
      if (!spoken) {
        setVoiceError('Não foi possível obter o texto desta gravação.');
        return;
      }
      setText((prev) => (prev.trim() ? `${prev.trim()} ${spoken}` : spoken).slice(0, 1000));
    } catch (err) {
      setVoiceError(err.message || 'Não foi possível obter o texto desta gravação.');
    }
  }

  const clock = `${Math.floor((voice.elapsedSec || 0) / 60)}:${String((voice.elapsedSec || 0) % 60).padStart(2, '0')}`;

  async function onPdf(item, index) {
    if (pdfBusy >= 0) return;
    setPdfBusy(index);
    setError('');
    try {
      const { parts, pending } = splitAnswer(item.content, item.figuras);
      await downloadOryaAnswerPdf({
        os,
        blocks: parts.concat(pending),
        fontes: item.fontes,
      });
    } catch (err) {
      setError(err.message || 'Não foi possível gerar o PDF.');
    } finally {
      setPdfBusy(-1);
    }
  }

  const canSend = Boolean(text.trim()) && ready && !busy && !voice.recording && !voice.busy;

  return (
    <aside className="order-details-mock-card odm-orya" aria-label="Suporte com Orya">
      <div className="odm-orya-list" ref={listRef}>
        {messages.length === 0 ? (
          <p className="odm-orya-empty">
            {ready
              ? 'Pergunte sobre o manual deste equipamento.'
              : 'A consulta aos manuais ainda não está liberada para o Pilot.'}
          </p>
        ) : null}
        {messages.map((item, index) => (
          <div key={`${item.role}-${index}`} className={`odm-orya-msg odm-orya-msg--${item.role}`}>
            {item.role === 'assistant'
              ? <AnswerBody os={os} content={item.content} figuras={item.figuras} />
              : <p>{item.content}</p>}
            {(item.fontes || []).length ? (
              <ul className="odm-orya-sources">
                {item.fontes.map((fonte) => (
                  <li key={`${fonte.documento}-${fonte.pagina}`}>{fonte.documento}{fonte.pagina ? ` · p. ${fonte.pagina}` : ''}</li>
                ))}
              </ul>
            ) : null}
            {(item.videos || []).map((video) => <YoutubeClip key={video.id} video={video} />)}
            {item.role === 'assistant' ? (
              <button type="button" className="odm-orya-pdf" disabled={pdfBusy >= 0} onClick={() => onPdf(item, index)}>
                {pdfBusy === index ? 'Gerando PDF…' : 'Gerar PDF'}
              </button>
            ) : null}
          </div>
        ))}
        {busy ? <p className="odm-orya-wait">Pensando…</p> : null}
      </div>
      {error ? <p className="odm-orya-error" role="alert">{error}</p> : null}
      {voice.recording || voice.busy ? (
        <div className="odm-orya-voice" role="status" aria-live="polite">
          <span className={`odm-orya-voice-pulse${voice.busy ? ' is-busy' : ''}`} aria-hidden />
          <span className="odm-orya-voice-timer">{clock}</span>
          <span>{voice.busy ? 'Processando…' : 'Gravando…'}</span>
          <button type="button" disabled={voice.busy} onClick={onTranscribe}>
            <Type size={13} strokeWidth={1.9} />
            Transcrever
          </button>
          <button type="button" disabled={voice.busy} aria-label="Cancelar gravação" onClick={() => voice.cancel()}>
            <X size={13} strokeWidth={2} />
          </button>
        </div>
      ) : null}
      {voiceError ? <p className="odm-orya-error" role="alert">{voiceError}</p> : null}
      <form className="odm-orya-form" onSubmit={send}>
        <textarea
          ref={fieldRef}
          rows={1}
          value={text}
          maxLength={1000}
          disabled={!ready || busy || voice.recording || voice.busy}
          placeholder={ready ? 'Dúvida sobre este equipamento' : 'Consulta indisponível'}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault();
              e.currentTarget.form?.requestSubmit();
            }
          }}
          aria-label="Pergunta para a Orya"
        />
        <button
          type={canSend ? 'submit' : 'button'}
          className={`odm-orya-mic${voice.recording || voice.busy ? ' is-recording' : ''}`}
          aria-label={canSend ? 'Enviar' : (voice.recording ? 'Gravando áudio' : 'Fala para texto')}
          aria-pressed={voice.recording}
          disabled={!ready || voice.busy || busy}
          onClick={canSend ? undefined : onMic}
        >
          {canSend ? <ArrowUp size={16} strokeWidth={2} /> : <Mic size={16} strokeWidth={2} />}
        </button>
      </form>
    </aside>
  );
}

export function OrderDetailsOryaButton({ open, onClick }) {
  return (
    <button type="button" className={`oosv2-obs-tool-btn${open ? ' is-open' : ''}`} aria-label="Obter suporte com Orya" aria-expanded={open} onClick={onClick}>
      <span className="oosv2-obs-tool-tip">Dúvidas do manual deste equipamento</span>
      <svg className="oosv2-obs-tool-ico" width="13" height="13" viewBox="0 0 16 16" fill="none" aria-hidden="true">
        <path d="M8 2l1.3 3.2L12.5 6.5 9.3 7.8 8 11 6.7 7.8 3.5 6.5 6.7 5.2 8 2z" stroke="currentColor" strokeWidth="1.3" strokeLinejoin="round" />
        <path d="M12.5 11.5l.6 1.4 1.4.6-1.4.6-.6 1.4-.6-1.4-1.4-.6 1.4-.6.6-1.4z" fill="currentColor" />
      </svg>
      <span>Obter suporte com Orya</span>
    </button>
  );
}
